/**
 * GenericModalComponent - Critical error dialogs for FR-035
 * Blocking modal for errors requiring immediate user attention with actionable guidance
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import type { ModalConfig, ModalAction } from '../../services/notification.service';

interface GenericModalProps {
  open: boolean;
  config: ModalConfig | null;
  onAction: (value: string) => void;
  onClose: () => void;
}

const SeverityIcon: React.FC<{ severity: ModalConfig['severity'] }> = ({ severity }) => {
  const iconProps = { sx: { fontSize: 40 } };
  switch (severity) {
    case 'error':
      return <ErrorOutlineIcon {...iconProps} color="error" />;
    case 'warning':
      return <WarningAmberIcon {...iconProps} color="warning" />;
    case 'success':
      return <CheckCircleOutlineIcon {...iconProps} color="success" />;
    case 'info':
    default:
      return <InfoOutlinedIcon {...iconProps} color="info" />;
  }
};

const getModalWidth = (width?: ModalConfig['width']): number => {
  switch (width) {
    case 'sm':
      return 400;
    case 'lg':
      return 600;
    case 'xl':
      return 800;
    case 'md':
    default:
      return 500;
  }
};

export const GenericModal: React.FC<GenericModalProps> = ({ open, config, onAction, onClose }) => {
  if (!config) return null;

  const handleClose = () => {
    if (config.closable !== false) {
      onClose();
    }
  };

  const handleAction = (action: ModalAction) => () => {
    onAction(action.value);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: getModalWidth(config.width),
          maxWidth: '95vw',
          borderRadius: 2,
          boxShadow: 24,
        },
      }}
      disableEscapeKeyDown={config.closable === false}
      aria-labelledby="generic-modal-title"
      aria-describedby="generic-modal-description"
    >
      <DialogTitle id="generic-modal-title" component="div">
        <Box display="flex" alignItems="center" gap={1.5}>
          <SeverityIcon severity={config.severity} />
          <Typography variant="h6" component="span" sx={{ flex: 1 }}>
            {config.title}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="generic-modal-description" component="div" sx={{ mt: 1 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {config.message}
          </Typography>
          {config.details && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 1,
                p: 1.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
                fontFamily: 'monospace',
              }}
            >
              {config.details}
            </Typography>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        {config.actions.map((action) => (
          <Button
            key={action.value}
            onClick={handleAction(action)}
            variant={action.primary ? 'contained' : 'outlined'}
            color={action.danger ? 'error' : action.primary ? 'primary' : 'inherit'}
            disabled={action.disabled}
            sx={{ minHeight: 44 }}
          >
            {action.label}
          </Button>
        ))}
      </DialogActions>
    </Dialog>
  );
};

export default GenericModal;
